import React from 'react';
import { Tooltip } from '@clockout-dev/design-elements';
import { describePayrollStatus, connectionsSuffix } from './payrollStatus.constants';
import styles from './styles/payroll-status-pill.module.scss';

/**
 * Payroll-connection status pill — the ONE rendering of `payrollStatus`, used
 * by the Reports Users table ("Payroll" column) and the user page (User
 * Details "Payroll" row), so a member reads the same on both. Label from
 * `payrollStatus.constants`, tone from the served classification
 * (`toneFor`), meaning in the tooltip and an accessible name — colour is
 * never the only signal.
 *
 * When the member has more than one connection a muted "N connections"
 * suffix follows the pill; with `onViewUser` it is a button to the user
 * page, where the connections are listed.
 *
 * @param {Object} props
 * @param {string | null | undefined} props.state `row.payrollStatus` / `consumer.payrollStatus`
 * @param {number | null | undefined} [props.connectionsCount]
 * @param {() => void} [props.onViewUser] click-through for the connections suffix
 * @param {string | null | undefined} [props.warning] `consumer.payrollStatusWarning` — why `state` is null
 */
export default function PayrollStatusPill({ state, connectionsCount, onViewUser, warning }) {
    const entry = describePayrollStatus(state, warning);
    const suffix = connectionsSuffix(connectionsCount);
    const clickable = suffix && typeof onViewUser === 'function';

    return (
        <span className={styles.wrap}>
            <Tooltip title={entry.description} mouseEnterDelay={0.05}>
                <span
                    className={`${styles.pill} ${styles[`tone_${entry.tone}`] || styles.tone_neutral}`}
                    role="status"
                    aria-label={`Payroll status: ${entry.label}`}
                    data-tone={entry.tone}
                >
                    <span className={styles.pillDot} />
                    {entry.label}
                </span>
            </Tooltip>
            {suffix ? (
                clickable ? (
                    <button
                        type="button"
                        className={`${styles.connections} ${styles.connectionsLink}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onViewUser();
                        }}
                        aria-label={`${suffix} — open the user page`}
                    >
                        {suffix}
                    </button>
                ) : (
                    <span className={styles.connections}>{suffix}</span>
                )
            ) : null}
        </span>
    );
}
